import { getApiClient } from "@src/api";
import { libraryQueryKeys } from "@src/components/library/queries/query-keys";
import {
	type LibraryListCardNode,
	libraryCardSchema,
} from "@src/components/library/schema";
import { graphql } from "@src/gql";
import { useInfiniteQuery } from "@tanstack/react-query";
import { z } from "zod";

const myCardLibraryQuery = graphql(/* GraphQL */ `
	query myCardLibrary($first: Int, $after: String) {
		library {
			cards(first: $first, after: $after) {
				nodes {
					createdAt
					id
					isPublished
					name
					ownerId
					position
					shareKey
					status
					streamPosition
				}
				pageInfo {
					hasNextPage
					hasPreviousPage
					startCursor
					endCursor
				}
			}
		}
	}
`);

export type LibraryCardNode = LibraryListCardNode;

const pageInfoSchema = z.object({
	hasNextPage: z.boolean(),
	hasPreviousPage: z.boolean().nullish(),
	startCursor: z.string().nullish(),
	endCursor: z.string().nullish(),
});

const libraryCardsConnectionSchema = z.object({
	nodes: z.array(libraryCardSchema),
	pageInfo: pageInfoSchema,
});

const libraryCardsResponseSchema = z.object({
	cards: libraryCardsConnectionSchema,
});

export const useMyCardLibrary = (limit = 24) => {
	const client = getApiClient();

	return useInfiniteQuery({
		queryKey: libraryQueryKeys.myCardLibrary.list(limit),
		queryFn: async ({ pageParam }) => {
			const result = await client.query({
				query: myCardLibraryQuery,
				variables: {
					first: limit,
					after: pageParam,
				},
			});

			if (result.error) {
				throw new Error(
					result.error.graphQLErrors[0]?.message ?? result.error.message,
				);
			}

			const parsed = libraryCardsResponseSchema.safeParse(result.data?.library);
			if (!parsed.success) {
				throw new Error(parsed.error.issues[0]?.message ?? "Invalid response");
			}

			return parsed.data.cards;
		},
		initialPageParam: undefined as string | undefined,
		getNextPageParam: (lastPage) =>
			lastPage.pageInfo.hasNextPage
				? (lastPage.pageInfo.endCursor ?? undefined)
				: undefined,
		staleTime: 1000 * 30,
		select: (data) => data.pages.flatMap((p) => p.nodes),
	});
};
